import { useEffect, useMemo, useRef, useState } from 'react';
import { FileArrowUp, X } from 'shared/icons';
import { FormatFileSize } from '../../utils/format';

function isImageFile(file) {
    return String(file?.type || '')
        .toLowerCase()
        .startsWith('image/');
}

const FileUploader = ({
    files = [],
    onFilesChange,
    multiple = true,
    accept = '*/*',
    maxFiles = 20,
    disabled = false,
    title = 'Arraste arquivos aqui ou selecione do dispositivo',
    subtitle = '',
}) => {
    const inputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    const normalizedFiles = useMemo(() => (Array.isArray(files) ? files : []), [files]);

    const previews = useMemo(() => {
        return normalizedFiles
            .map((file, index) => {
                if (!isImageFile(file)) {
                    return {
                        id: `${file?.name || 'file'}-${index}`,
                        file,
                        previewUrl: null,
                    };
                }

                return {
                    id: `${file?.name || 'image'}-${file?.lastModified || index}-${index}`,
                    file,
                    previewUrl: URL.createObjectURL(file),
                };
            })
            .filter(Boolean);
    }, [normalizedFiles]);

    useEffect(() => {
        return () => {
            previews.forEach((entry) => {
                if (entry.previewUrl) {
                    URL.revokeObjectURL(entry.previewUrl);
                }
            });
        };
    }, [previews]);

    const updateFiles = (nextFiles) => {
        if (typeof onFilesChange === 'function') {
            onFilesChange(nextFiles);
        }
    };

    const appendFiles = (incoming) => {
        const list = Array.from(incoming || []);
        if (list.length === 0) {
            return;
        }

        const next = multiple ? [...normalizedFiles, ...list].slice(0, maxFiles) : [list[0]];
        updateFiles(next);
    };

    const removeAt = (indexToRemove) => {
        const next = normalizedFiles.filter((_, index) => index !== indexToRemove);
        updateFiles(next);
    };

    const onDrop = (event) => {
        event.preventDefault();
        if (disabled) {
            setDragActive(false);
            return;
        }

        setDragActive(false);
        appendFiles(event.dataTransfer?.files || []);
    };

    return (
        <div className="space-y-3">
            <div
                className={`rounded-xl border-2 border-dashed p-5 transition-colors ${
                    dragActive
                        ? 'border-success-500 bg-success-50 dark:border-success-400 dark:bg-success-950/30'
                        : 'border-mono-200 bg-mono-50 dark:border-mono-800 dark:bg-mono-950'
                } ${disabled ? 'opacity-60' : ''}`}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={() => {
                    if (!disabled) {
                        inputRef.current?.click();
                    }
                }}
                onKeyDown={(event) => {
                    if (disabled) {
                        return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    if (!disabled) {
                        setDragActive(true);
                    }
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple={multiple}
                    accept={accept}
                    className="hidden"
                    disabled={disabled}
                    onChange={(event) => {
                        appendFiles(event.target.files || []);
                        event.target.value = '';
                    }}
                />

                <div className="flex flex-col gap-3 text-center">
                    <div className="mx-auto rounded-full bg-white p-3 text-mono-500 shadow-sm dark:bg-black dark:text-mono-300">
                        <FileArrowUp size={24} />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-black dark:text-white">{title}</p>
                        {subtitle ? <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">{subtitle}</p> : null}
                        <p className="mt-1 text-xs text-mono-500 dark:text-mono-400">
                            Toque ou clique na area para selecionar arquivos
                        </p>
                    </div>
                </div>
            </div>

            {previews.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {previews.map((entry, index) => (
                        <div
                            key={entry.id}
                            className="relative aspect-square overflow-hidden rounded-lg border border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950"
                        >
                            {entry.previewUrl ? (
                                <img
                                    src={entry.previewUrl}
                                    alt={entry.file?.name || `arquivo-${index + 1}`}
                                    className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center px-2 text-center">
                                    <span className="mb-1 rounded-full bg-mono-200 px-2 py-0.5 text-[10px] font-semibold uppercase text-mono-700 dark:bg-mono-800 dark:text-mono-300">
                                        Sem preview
                                    </span>
                                    <span className="text-xs text-mono-600 dark:text-mono-300">Arquivo sem miniatura</span>
                                </div>
                            )}
                            <div className="px-2 py-1.5">
                                <p className="truncate text-[11px] text-mono-700 dark:text-mono-200">
                                    {entry.file?.name || '-'}
                                </p>
                                <p className="text-[10px] text-mono-500 dark:text-mono-400">
                                    {FormatFileSize(entry.file?.size)}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="absolute right-1 top-1 inline-flex items-center justify-center rounded bg-black/70 p-1 text-white hover:bg-black pointer-coarse:min-h-11 pointer-coarse:min-w-11"
                                onClick={() => removeAt(index)}
                                aria-label="Remover arquivo"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default FileUploader;
